import React, { useCallback } from 'react'
import { Box, SxProps, Theme, styled } from '@mui/material'
import {
  DewiColorsGray8,
  DewiColorsGreen7,
  DewiColorsRed7,
  DewiColorsDark8,
  DewiSpacing4,
  DewiRadiusRounded,
  DewiSpacing5,
  DewiSpacing0,
  DewiColorsGray0,
  DewiColorsOrange5,
  DewiColorsViolet5,
  DewiColorsPink5,
  DewiColorsCyan5,
} from '@styles/tokens/dist/js/tokens'

const ColorGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  height: DewiSpacing4,
  width: '-webkit-fill-available',
  justifyContent: 'flex-end',
})

const baseColorStyle = {
  height: '100%',
  objectFit: 'fill',
  borderRadius: DewiRadiusRounded,
}

const beforeCircle = (hasMargin: boolean) => {
  if (!hasMargin) return {}

  return {
    ['&::before']: {
      content: '""',
      position: 'absolute',
      left: '0px',
      bottom: '0px',
      width: '11.2px',
      height: '11.2px',
      background: DewiColorsDark8,
      borderRadius: '500px',
      // clip half of circle on left side
      transform: 'translate(-15%, 0%)',
    },
  }
}

const CyanColor = (percent: number, hasMargin: boolean, index: number) => ({
  ...baseColorStyle,
  width: `${percent}%`,
  minWidth: percent > 0 ? DewiSpacing5 : DewiSpacing0,
  backgroundColor: DewiColorsCyan5,
  zIndex: 40 - index * 10,
  display: percent > 0 ? 'block' : 'none',
  marginLeft: hasMargin ? `-4.5px` : DewiSpacing0,
  ...beforeCircle(hasMargin),
})

const GreenColor = (percent: number, hasMargin: boolean, index: number) => ({
  ...baseColorStyle,
  width: `${percent}%`,
  minWidth: percent > 0 ? DewiSpacing5 : DewiSpacing0,
  backgroundColor: DewiColorsGreen7,
  zIndex: 40 - index * 10,
  display: percent > 0 ? 'block' : 'none',
  marginLeft: hasMargin ? `-4.5px` : DewiSpacing0,
  ...beforeCircle(hasMargin),
})
const WhiteColor = (percent: number, hasMargin: boolean, index: number) => ({
  ...baseColorStyle,
  width: `${percent}%`,
  minWidth: percent > 0 ? DewiSpacing5 : DewiSpacing0,
  backgroundColor: DewiColorsGray0,
  marginLeft: hasMargin ? `-4.5px` : DewiSpacing0,
  zIndex: 40 - index * 10,
  display: percent > 0 ? 'block' : 'none',
  ...beforeCircle(hasMargin),
})

const OrangeColor = (percent: number, hasMargin: boolean, index: number) => ({
  ...baseColorStyle,
  width: `${percent}%`,
  minWidth: percent > 0 ? DewiSpacing5 : DewiSpacing0,
  backgroundColor: DewiColorsOrange5,
  marginLeft: hasMargin ? `-4.5px` : DewiSpacing0,
  zIndex: 40 - index * 10,
  display: percent > 0 ? 'block' : 'none',
  ...beforeCircle(hasMargin),
})

const VioletColor = (percent: number, hasMargin: boolean, index: number) => ({
  ...baseColorStyle,
  width: `${percent}%`,
  minWidth: percent > 0 ? DewiSpacing5 : DewiSpacing0,
  backgroundColor: DewiColorsViolet5,
  marginLeft: hasMargin ? `-4.5px` : DewiSpacing0,
  zIndex: 40 - index * 10,
  display: percent > 0 ? 'block' : 'none',
  ...beforeCircle(hasMargin),
})

const RedColor = (percent: number, hasMargin: boolean, index: number) => ({
  ...baseColorStyle,
  width: `${percent}%`,
  minWidth: percent > 0 ? DewiSpacing5 : DewiSpacing0,
  backgroundColor: DewiColorsRed7,
  marginLeft: hasMargin ? `-4.5px` : DewiSpacing0,
  zIndex: 40 - index * 10,
  display: percent > 0 ? 'block' : 'none',
  ...beforeCircle(hasMargin),
})

const PinkColor = (percent: number, hasMargin: boolean, index: number) => ({
  ...baseColorStyle,
  width: `${percent}%`,
  minWidth: percent > 0 ? DewiSpacing5 : DewiSpacing0,
  backgroundColor: DewiColorsPink5,
  marginLeft: hasMargin ? `-4.5px` : DewiSpacing0,
  zIndex: 40 - index * 10,
  display: percent > 0 ? 'block' : 'none',
  ...beforeCircle(hasMargin),
})

const GrayColor = (percent: number, hasMargin: boolean, index: number) => ({
  ...baseColorStyle,
  width: `${percent}%`,
  backgroundColor: DewiColorsGray8,
  zIndex: 40 - index * 10,
  marginLeft: hasMargin ? `-4.5px` : DewiSpacing0,
  display: percent > 0 ? 'block' : 'none',
  ...beforeCircle(hasMargin),
})

export type ColorSection = {
  colorType:
    | 'green'
    | 'red'
    | 'gray'
    | 'white'
    | 'orange'
    | 'violet'
    | 'pink'
    | 'cyan'
  percent: number
  isActive: boolean
}

export interface ColorBarProps {
  sections: ColorSection[]
  sx?: SxProps<Theme>
}

const ColorBar = ({ sections, sx }: ColorBarProps) => {
  const boxProps = useCallback((section: ColorSection, index: number) => {
    const isFirst = index === 0
    switch (section.colorType) {
      case 'green':
        return GreenColor(section.percent, !isFirst, index)
      case 'red':
        return RedColor(section.percent, !isFirst, index)
      case 'gray':
        return GrayColor(section.percent, !isFirst, index)
      case 'white':
        return WhiteColor(section.percent, !isFirst, index)
      case 'orange':
        return OrangeColor(section.percent, !isFirst, index)
      case 'violet':
        return VioletColor(section.percent, !isFirst, index)
      case 'pink':
        return PinkColor(section.percent, !isFirst, index)
      case 'cyan':
        return CyanColor(section.percent, !isFirst, index)
    }
  }, [])

  return (
    <ColorGroup sx={{ ...sx, display: 'flex', justifyContent: 'flex-start' }}>
      {sections?.map((section, index) => (
        <Box
          key={index}
          position={'relative'}
          sx={{
            ...boxProps(section, index),
            opacity: section.isActive ? 1.0 : 0.25,
          }}
        />
      ))}
    </ColorGroup>
  )
}

export default ColorBar
